import { LoadingCircle } from "../common";
import HeartIcon from "../../assets/heart.svg";
import { useNavigate } from "react-router-dom";
import EditIcon from "../../assets/edit-icon.svg";
import TargetIcon from "../../assets/targetIcon.svg";
import { formatPrice } from "../../utils/formatPrice";
import { formatDate } from "../../utils/dateFormatter";
import EquifaxIcon from "../../assets/equifax-icon.svg";
import DirectionIcon from "../../assets/directionIcon.svg";
import { getSavingsDetails } from "../../services/homeGoal";
import CheckVerifiedIcon from "../../assets/check-verified.svg";
import { useUserProfileStore } from "../../stores/profileStore";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SavingsSelectedCitiesEnum } from "../../savingsSelectedCities";
import {
	propertyTypeLabels,
	type SavingsDetails,
} from "../../interfaces/mainProfile";

const SavingsView = () => {
	const navigate = useNavigate();
	const { userProfile } = useUserProfileStore();
	const [preview, setPreview] = useState(true);
	const [creditScore, setCreditScore] = useState<number | null>(null);
	const [loading, setLoading] = useState(false);
	const [savingsDetails, setSavingsDetails] = useState<SavingsDetails | null>(
		null,
	);
	const goal = userProfile?.homeGoal;

	const hasCompleteGoal =
		!!goal?.targetAmount &&
		(goal.propertyTypes?.length ?? 0) > 0 &&
		(goal.selectedCities?.length ?? 0) > 0;

	useEffect(() => {
		async function fetchSavings() {
			try {
				setLoading(true);
				const savingsDetails = await getSavingsDetails();
				setSavingsDetails(savingsDetails);
				setCreditScore(savingsDetails?.creditScore);
				setLoading(false);
			} catch (error) {
				setLoading(false);
				console.error("Error fetching savings details:", error);
			}
		}
		if (hasCompleteGoal) {
			fetchSavings();
		}
	}, [hasCompleteGoal]);

	useEffect(() => {
		setPreview(!hasCompleteGoal);
	}, [hasCompleteGoal]);

	const strokeWidth = 12;
	const circleChartSize = 120;
	const maxScore = 900;
	const radius = (circleChartSize - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;

	const pct = Math.min(Math.max((creditScore ?? 0) / maxScore, 0), 1);
	const offset = circumference * (1 - pct);

	const savingsAmt = savingsDetails?.savings?.amount ?? 0;
	const neededAmt = savingsDetails?.savingsNeeded?.amount ?? 0;
	const percentage =
		neededAmt > 0
			? Math.min(100, Math.round((savingsAmt / neededAmt) * 100))
			: 0;

	const rawTypes = userProfile?.homeGoal?.propertyTypes ?? [];
	const MAX_LEN = 20;

	let displayString: string;
	if (rawTypes.length === 0) {
		displayString = "N/A";
	} else {
		const labels = rawTypes.map((t) => propertyTypeLabels[t] || t);
		const fullString = labels.join(", ");
		displayString =
			fullString.length > MAX_LEN
				? `${fullString.slice(0, MAX_LEN)}…`
				: fullString;
	}

	const selectedCityKey = userProfile?.homeGoal?.selectedCities?.[0];
	const selectedCityValue = selectedCityKey
		? SavingsSelectedCitiesEnum[
				selectedCityKey as keyof typeof SavingsSelectedCitiesEnum
			]
		: "N/A";

	const barRef = useRef<HTMLDivElement>(null);
	const labelRef = useRef<HTMLDivElement>(null);
	const [labelLeftPx, setLabelLeftPx] = useState(0);

	useLayoutEffect(() => {
		if (!barRef.current || !labelRef.current) return;
		const barWidth = barRef.current.clientWidth;
		const labelWidth = labelRef.current.clientWidth;

		const desired = (percentage / 100) * barWidth;
		const clamped = Math.min(Math.max(0, desired), barWidth - labelWidth);
		setLabelLeftPx(clamped);
	}, [percentage]);

	return (
    <div
      className={`${preview && !loading && "h-full"} overflow-auto px-4 pb-6 pt-2`}
    >
      {loading ? (
        <div className="flex flex-col items-center gap-10 justify-center h-full">
          <LoadingCircle />
        </div>
      ) : (
        <>
          {preview ? (
            <div className="flex flex-col items-center gap-10 justify-center h-full">
              <div className="text-3xl text-black font-[900] w-full">
                Build Your
                <br />
                <span className="text-z-dark-moss-green">
                  Home-Savings Plan
                </span>
              </div>
              <div className="w-full flex flex-col gap-4">
                <div className="w-full rounded-[20px] bg-[#D8F8C5] flex items-center gap-2 px-3 py-4">
                  <div className="p-2 rounded-full bg-white">
                    <img src={DirectionIcon} alt="" />
                  </div>
                  <div className="text-black text-sm font-semibold">
                    Take control of your home-buying journey—no more guessing
                  </div>
                </div>
                <div className="w-full rounded-[20px] bg-[#EFECFF] flex items-center gap-2 px-3 py-4">
                  <div className="p-2 rounded-full bg-white">
                    <img src={TargetIcon} alt="" />
                  </div>
                  <div className="text-black text-sm font-semibold">
                    Track your progress with simple milestones and reminders.
                  </div>
                </div>
                <div className="w-full rounded-[20px] bg-[#FDD] flex items-center gap-2 px-3 py-4">
                  <div className="p-2 rounded-full bg-white">
                    <img src={HeartIcon} alt="" />
                  </div>
                  <div className="text-black text-sm font-semibold">
                    Receive personalized tips until you're ready to buy.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  window
                    ._sendEvent("ZownCaAppSavingsStart")
                    .catch((error: unknown) => {
                      console.error(error);
                    })
                    .finally(() => {
                      navigate("/savings-input-flow/mortgage-iframe");
                    });
                }}
                className="text-white font-medium bg-black rounded-full py-3 w-full flex items-center gap-2 justify-center active:opacity-70"
              >
                <img src={CheckVerifiedIcon} alt="" />
                Get Started
              </button>
            </div>
          ) : (
            <div className="flex flex-col w-full text-black space-y-6">
              <div className="w-full grid grid-cols-2 gap-4">
                <div className="px-4 py-6 rounded-2xl flex items-center gap-2 justify-between bg-[#FFF2BF] col-span-2 w-full">
                  <div className="space-y-2 w-full">
                    <div className="w-full flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="font-bold">Affordability</div>
                        <div className="text-xs">What you could afford now</div>
                      </div>
                    </div>
                    <div className="font-bold text-5xl">
                      {savingsDetails?.affordability
                        ? `$${formatPrice(
                            convertToFormattedAmount(
                              savingsDetails?.affordability?.amount
                            )
                          )}`
                        : "N/A"}
                    </div>
                  </div>
                </div>
                <div
                  className="bg-[#DAFFD3] p-4 flex justify-between w-full items-center gap-2 text-start rounded-2xl"
                  onClick={() => {
                    navigate("/savings-input-flow/mortgage-iframe", {
                      state: { from: "savings-view" },
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate("/savings-input-flow/mortgage-iframe", {
                        state: { from: "savings-view" },
                      });
                    }
                  }}
                >
                  <div>
                    <div className="font-bold">Income</div>
                    <div className="font-bold">
                      {savingsDetails?.income
                        ? `$${formatPrice(
                            convertToFormattedAmount(
                              savingsDetails?.income?.amount
                            )
                          )}`
                        : "N/A"}
                    </div>
                  </div>
                  <button type="button" className="flex items-center gap-2">
                    <div className="bg-white p-2 rounded-full">
                      <img src={EditIcon} alt="" />
                    </div>
                  </button>
                </div>
                <div
                  className="bg-[#DCEFFF] p-4 flex items-center justify-between gap-2 text-start rounded-2xl"
                  onClick={() => {
                    navigate("/savings-input-flow/mortgage-iframe", {
                      state: { from: "savings-view" },
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate("/savings-input-flow/mortgage-iframe", {
                        state: { from: "savings-view" },
                      });
                    }
                  }}
                >
                  <div>
                    <div className="font-bold">Savings</div>
                    <div className="font-bold">
                      {savingsDetails?.savings
                        ? `$${formatPrice(
                            convertToFormattedAmount(
                              savingsDetails?.savings?.amount
                            )
                          )}`
                        : "N/A"}
                    </div>
                  </div>
                  <button type="button" className="flex items-center gap-2">
                    <div className="bg-white p-2 rounded-full">
                      <img src={EditIcon} alt="" />
                    </div>
                  </button>
                </div>

                <div
                  className={`rounded-2xl px-4 py-6 col-span-2 bg-[#F8F8F8] flex items-center gap-2 justify-between ${!creditScore && "hidden"}`}
                >
                  <div className="flex flex-col justify-between h-full gap-6">
                    <div className="space-y-1">
                      <div className="font-bold">Your Credit Score</div>
                      <img src={EquifaxIcon} alt="" />
                    </div>
                    {savingsDetails?.creditScoreLastUpdatedAt && (
                      <div className="text-xs">
                        Updated{" "}
                        {formatDate(savingsDetails?.creditScoreLastUpdatedAt)}
                      </div>
                    )}
                  </div>
                  {/* biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                  <svg
                    width={circleChartSize}
                    height={circleChartSize}
                    className="block"
                  >
                    <circle
                      cx={circleChartSize / 2}
                      cy={circleChartSize / 2}
                      r={radius}
                      stroke="#FFFFFF"
                      strokeWidth={strokeWidth}
                      fill="transparent"
                      transform={`rotate(-90 ${circleChartSize / 2} ${circleChartSize / 2})`}
                    />

                    <circle
                      cx={circleChartSize / 2}
                      cy={circleChartSize / 2}
                      r={radius}
                      stroke="#191D16"
                      strokeWidth={strokeWidth}
                      fill="transparent"
                      strokeDasharray={circumference}
                      strokeDashoffset={offset}
                      strokeLinecap="round"
                      style={{ transition: "stroke-dashoffset 0.6s ease" }}
                      transform={`rotate(-90 ${circleChartSize / 2} ${circleChartSize / 2})`}
                    />

                    <text
                      x="50%"
                      y="50%"
                      dy="0.35em"
                      textAnchor="middle"
                      fontSize={circleChartSize * 0.25}
                      fill="#191D16"
                      fontWeight="bold"
                    >
                      {creditScore}
                    </text>
                  </svg>
                </div>
              </div>
              <div className="rounded-2xl bg-[#F5FEEF] p-4 space-y-10">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-bold">Home Savings Goal</div>
                    {savingsDetails?.savingsNeeded && (
                      <div className="rounded-full text-white bg-z-dark-moss-green py-1 px-3">
                        $
                        {formatPrice(
                          convertToFormattedAmount(
                            savingsDetails?.savingsNeeded?.amount
                          )
                        )}
                      </div>
                    )}
                  </div>
                  {userProfile?.homeGoal?.targetAmount && (
                    <div className="text-xs max-w-[190px] text-black/90">
                      The minimum amount of savings needed for a $
                      {formatPrice(
                        convertToFormattedAmount(
                          userProfile?.homeGoal?.targetAmount.amount
                        )
                      )}{" "}
                      home
                    </div>
                  )}
                  {savingsDetails?.savings && savingsDetails?.savingsNeeded && (
                    <div className="w-full bg-white rounded-full h-6 relative">
                      <div
                        ref={barRef}
                        className="bg-z-dark-moss-green h-6 rounded-full text-white flex items-center justify-center text-xs font-semibold"
                        style={{
                          width: `${percentage}%`,
                        }}
                      >
                        {percentage > 30 && `${percentage}%`}
                      </div>
                      <div
                        ref={labelRef}
                        className="absolute translate-y-1/2 -bottom-1/2 text-xs font-semibold"
                        style={{ left: labelLeftPx }}
                      >
                        {savingsDetails?.savings
                          ? `$${formatPrice(
                              convertToFormattedAmount(
                                savingsDetails?.savings?.amount
                              )
                            )}`
                          : "N/A"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="rounded-md py-2 px-3 space-y-4 bg-white">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-[#32302FBF]/70">
                        Home Price
                      </div>
                      <div className="font-semibold">
                        {userProfile?.homeGoal?.targetAmount
                          ? `$${formatPrice(
                              convertToFormattedAmount(
                                userProfile?.homeGoal?.targetAmount.amount
                              )
                            )}`
                          : "N/A"}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-[#32302FBF]/70">
                        Location
                      </div>
                      <div className="font-semibold">{selectedCityValue}</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-[#32302FBF]/70">
                        Property Type
                      </div>
                      <div className="font-semibold">{displayString}</div>
                    </div>
                  </div>
                  <div className="border-t border-[#D9D9D9] py-2 space-y-2 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/savings-input-flow/city-selection");
                      }}
                      className="py-2 px-2 w-full border-2 border-black rounded-lg flex items-center gap-2 justify-center text-black font-bold"
                    >
                      <img src={EditIcon} alt="" />
                      Edit my goal
                    </button>
                    <div className="text-[8px] text-[#32302FBF]/70 w-full">
                      All figures are estimates. Final savings goals and buying
                      power are subject to lender approval and eligibility.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { SavingsView };
